import {
    styled,
    Box,
    Paper,
    IconButton,
    TextField,
    Grid,
    RadioGroup,
} from '../../../../base';

export const StyledFormContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%', // Make form take full height
}));

export const StyledTopControlsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    paddingInlineStart: theme.spacing(3),
}));

export const StyledQuestionsListContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isConfiguration',
})<{ isConfiguration?: boolean }>(({ theme, isConfiguration = true }) => ({
    flexGrow: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: isConfiguration ? '600px' : '200px',
    paddingInlineEnd: theme.spacing(1),
}));

export const StyledAddQuestionEndContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(1),
}));

export const StyledFormActionsContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
}));

// --- SortableQuestion Styles ---

export const StyledSortableQuestionWrapper = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    position: 'relative',
}));

export const StyledQuestionPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    position: 'relative',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
}));

export const StyledDragHandle = styled(Box)(({ theme }) => ({
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    paddingInlineStart: theme.spacing(1),
    paddingInlineEnd: theme.spacing(0.5),
    '&:active': {
        cursor: 'grabbing',
    },
}));

export const StyledPlaceholderBox = styled(Box)(({ theme }) => ({
    width: theme.spacing(3),
    height: theme.spacing(3),
}));

export const StyledQuestionFieldsGrid = styled(Grid)(() => ({
    flexGrow: 1,
    alignItems: 'flex-start',
}));

export const StyledQuestionField = styled(TextField)(() => ({
    width: '100%',
}));

export const StyledQuestionActionPlaceholder = styled(Box)(({ theme }) => ({
    width: theme.spacing(5),
}));

export const StyledAddQuestionButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    bottom: -14,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
}));

export const StyledPageHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    paddingBottom: theme.spacing(2),
}));

// --- Sub-component Styles ---

export const StyledQuestionMainContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    width: '100%',
    paddingTop: theme.spacing(5),
    [theme.breakpoints.up('sm')]: {
        paddingTop: theme.spacing(1.5),
    },
}));

export const StyledQuestionFieldsContainer = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginInlineStart: theme.spacing(5),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        marginInlineStart: theme.spacing(4),
    },
}));

export const StyledQuestionTitleWrapper = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    minWidth: '200px',
    maxWidth: '35%',
    [theme.breakpoints.down('sm')]: {
        minWidth: '100%',
    },
}));

export const StyledQuestionTypeWrapper = styled(Box)(({ theme }) => ({
    width: '200px',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}));

export const StyledTogglesContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexShrink: 0,
    width: 'auto',
    marginInlineStart: 0,
    paddingTop: '8px',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: theme.spacing(1),
        alignItems: 'flex-start',
        paddingTop: 0,
        width: '100%',
    },
}));

export const StyledToggleItem = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.15,
}));

export const StyledActionButtonsContainer = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'flex-start',
    gap: 0,
    paddingTop: '4px',
    [theme.breakpoints.up('sm')]: {
        display: 'flex',
    },
}));

export const StyledActionButtonsMobile = styled(Box)(({ theme }) => ({
    display: 'flex',
    position: 'absolute',
    top: theme.spacing(1),
    insetInlineEnd: theme.spacing(1),
    gap: 0,
    zIndex: 2,
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

export const StyledDragHandleWrapper = styled(Box)(() => ({
    position: 'absolute',
    insetInlineStart: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
}));

export const StyledOptionsContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    paddingInlineStart: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

export const StyledOptionsListWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
}));

export const StyledOptionItemWrapper = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    alignItems: 'flex-start',
}));

export const StyledOptionItemContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    maxWidth: '200px',
}));

export const StyledOptionInputWrapper = styled(Box)(() => ({
    width: '120px',
}));

export const StyledAddOptionButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

export const StyledEmptyStateContainer = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(4),
}));

export const StyledContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: theme.spacing(2),
}));

export const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
    flexWrap: 'wrap',
    columnGap: theme.spacing(2),
}));

export const StyledFileDisplayContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
}));

export const StyledFileLink = styled('a')(({ theme }) => ({
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
}));
